import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Banner } from '../ui/Banner';
import SupportRequestModal from '../help/SupportRequestModal';
import { useToast } from '../ui/Toast';
import { LockIcon } from '../../ui/icons';
import { useTranslation } from '../../translations';
import { formatDate, STREFA_FIRMY } from '../../utils/format';
import { KLUCZ_PRZERWY, przerwaZUzytkownika, zainstalujNasluchPrzerwy, zapiszFlagePrzerwy } from '../../utils/kontoWPrzerwie';
import { API_BASE } from '../../config';

// Przerwa w sezonie (FAZA F, F.11) i rezygnacja (F.25): konto tylko do odczytu. Jedna obsługa na
// cały panel: dostawca w App.js zna stan konta z sesji (logowanie, /api/whoami) i z każdej odpowiedzi
// 403 `account_paused`, pokazuje baner (przerwa: prośba o wznowienie; rezygnacja: eksport danych,
// termin usunięcia i prośba o odwołanie), a ekrany wyłączają przyciski zapisu przez
// `useKontoWPrzerwie()` albo `<TylkoOdczytWPrzerwie>`. Zapis i tak blokuje serwer.
// `wPrzerwie` znaczy „tylko do odczytu” z dowolnego powodu, a powód niesie `powod`.

const PUSTY = Object.freeze({
  wPrzerwie: false,
  powod: null,
  zaplanowana: false,
  rezygnacjaZaplanowana: false,
  pausedAt: null,
  pausedUntil: null,
  resignedAt: null,
  dataDeletionFrom: null,
  administrator: false,
  prosOWznowienie: null,
  prosOOdwolanie: null,
});
const KontoWPrzerwieContext = createContext(PUSTY);

// Komunikat po odmowie zapisu najwyżej raz na tyle milisekund: ekran potrafi wysłać kilka zapisów naraz.
const ODSTEP_KOMUNIKATU_MS = 8000;
// W trybie tylko do odczytu stan konta sprawdzamy ponownie (/api/whoami) co tyle i po powrocie do
// karty: przerwę kończy i rezygnację odwołuje super-admin, a dane sesji panel pobiera tylko przy
// starcie, więc otwarta karta zostawałaby tylko do odczytu aż do przeładowania strony.
const ODSTEP_SPRAWDZENIA_MS = 5 * 60 * 1000;

// `axiosInstance` podaje App.js: import axios tutaj ładowałby go w testach każdego ekranu z blokadą.
export function KontoWPrzerwieProvider({ user, axiosInstance = null, children }) {
  const { t } = useTranslation();
  const toast = useToast();
  const zSesji = useMemo(() => przerwaZUzytkownika(user), [user]);
  // Odmowa z serwera w trakcie pracy: przerwę albo rezygnację włączono, gdy karta była już otwarta.
  const [zOdmowy, setZOdmowy] = useState(null);
  // Świeższy stan z ponownego odczytu sesji, gdy karta jest otwarta w trybie tylko do odczytu.
  const [zSerwera, setZSerwera] = useState(null);
  // Okno zgłoszenia H.1: null, 'wznowienie' (po przerwie) albo 'odwolanie' (rezygnacji).
  const [okno, setOkno] = useState(null);
  const ostatniKomunikat = useRef(0);
  const powiadom = useRef(null);
  powiadom.current = (odmowa) => {
    setZOdmowy({ pausedUntil: odmowa.pausedUntil, powod: odmowa.powod || 'przerwa', deleteFrom: odmowa.deleteFrom || null });
    try { window.localStorage.setItem(KLUCZ_PRZERWY, '1'); } catch (_) { /* tylko wygoda */ }
    const teraz = Date.now();
    if (teraz - ostatniKomunikat.current < ODSTEP_KOMUNIKATU_MS) return;
    ostatniKomunikat.current = teraz;
    toast.warning(odmowa.message
      || (odmowa.powod === 'rezygnacja' ? t('rezygnacja.zapisZablokowany') : t('przerwa.zapisZablokowany')));
  };

  const userId = user && user.userId;
  useEffect(() => { setZOdmowy(null); }, [userId]);
  useEffect(() => { setZSerwera(null); }, [zSesji]);
  useEffect(() => (userId
    ? zainstalujNasluchPrzerwy((odmowa) => powiadom.current(odmowa), { ax: axiosInstance })
    : undefined), [userId, axiosInstance]);

  const stan = zSerwera || zSesji;
  const wPrzerwie = stan.wPrzerwie || !!zOdmowy;
  // Odmowa z serwera jest świeższa niż dane sesji (ponowny odczyt sesji ją zdejmuje, niżej).
  const powod = wPrzerwie ? ((zOdmowy && zOdmowy.powod) || stan.powod || 'przerwa') : null;

  // Komunikat po końcu trybu zależy od tego, co się skończyło: przerwa albo rezygnacja (odwołana).
  const poWznowieniu = useRef(null);
  poWznowieniu.current = () => toast.success(powod === 'rezygnacja' ? t('rezygnacja.odwolana') : t('przerwa.wznowiona'));
  useEffect(() => {
    if (!userId || !wPrzerwie) return undefined;
    let aktywny = true;
    // Powrót do karty daje naraz `focus` i `visibilitychange`: jedno sprawdzenie, jeden komunikat.
    let wToku = false;
    const sprawdz = async () => {
      if (wToku) return;
      wToku = true;
      try {
        const res = await fetch(`${API_BASE}/api/whoami`, { credentials: 'include' });
        if (!res.ok) return;
        const dane = await res.json();
        if (!aktywny || !dane || !dane.user || String(dane.userId) !== String(userId)) return;
        const nowy = przerwaZUzytkownika(dane.user);
        setZSerwera((stary) => (stary && JSON.stringify(stary) === JSON.stringify(nowy) ? stary : nowy));
        // Świeży stan z serwera zastępuje to, co wiedzieliśmy z odmowy zapisu.
        setZOdmowy(null);
        if (!nowy.wPrzerwie) {
          zapiszFlagePrzerwy(window.localStorage, dane.user);
          poWznowieniu.current();
        }
      } catch (_) {
        /* sieć: spróbujemy przy następnym powrocie do karty */
      } finally {
        wToku = false;
      }
    };
    const poPowrocie = () => { if (document.visibilityState !== 'hidden') sprawdz(); };
    document.addEventListener('visibilitychange', poPowrocie);
    window.addEventListener('focus', poPowrocie);
    const zegar = setInterval(sprawdz, ODSTEP_SPRAWDZENIA_MS);
    return () => {
      aktywny = false;
      document.removeEventListener('visibilitychange', poPowrocie);
      window.removeEventListener('focus', poPowrocie);
      clearInterval(zegar);
    };
  }, [userId, wPrzerwie]);

  // Eksport danych firmy pobiera wyłącznie administrator (serwer sprawdza to sam, F.12).
  const administrator = !!(user && (user.isAdmin || user.isSuperAdmin));
  const wartosc = useMemo(() => ({
    wPrzerwie,
    powod,
    zaplanowana: !wPrzerwie && stan.zaplanowana,
    rezygnacjaZaplanowana: !wPrzerwie && stan.rezygnacjaZaplanowana,
    pausedAt: stan.pausedAt,
    pausedUntil: (zOdmowy && zOdmowy.pausedUntil) || stan.pausedUntil,
    resignedAt: stan.resignedAt,
    // Dzień, od którego dane mogą zostać usunięte: tylko gdy serwer go podał (sesja albo odmowa).
    dataDeletionFrom: (zOdmowy && zOdmowy.deleteFrom) || stan.dataDeletionFrom || null,
    administrator,
    prosOWznowienie: () => setOkno('wznowienie'),
    prosOOdwolanie: () => setOkno('odwolanie'),
  }), [wPrzerwie, powod, stan, zOdmowy, administrator]);

  const wstepne = useMemo(() => {
    if (okno === 'odwolanie') {
      return { kategoria: 'question', temat: t('rezygnacja.odwolanie.temat'), tresc: t('rezygnacja.odwolanie.tresc') };
    }
    if (okno === 'wznowienie') {
      return { kategoria: 'question', temat: t('przerwa.wznowienie.temat'), tresc: t('przerwa.wznowienie.tresc') };
    }
    return null;
  }, [okno, t]);

  return (
    <KontoWPrzerwieContext.Provider value={wartosc}>
      {children}
      <SupportRequestModal
        open={!!okno}
        kategoriaStartowa="question"
        wstepne={wstepne}
        onClose={() => setOkno(null)}
        user={user}
        elevated
      />
    </KontoWPrzerwieContext.Provider>
  );
}

/**
 * `{ wPrzerwie, powod, zaplanowana, rezygnacjaZaplanowana, pausedAt, pausedUntil, resignedAt,
 * dataDeletionFrom, administrator, prosOWznowienie, prosOOdwolanie }`; poza dostawcą konto bez trybu.
 */
export function useKontoWPrzerwie() {
  return useContext(KontoWPrzerwieContext);
}

/** Baner nad stronami panelu: przerwa albo rezygnacja trwa albo jest zaplanowana. */
export function PrzerwaBanner() {
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const {
    wPrzerwie, powod, zaplanowana, rezygnacjaZaplanowana, pausedAt, pausedUntil, resignedAt, dataDeletionFrom,
    administrator, prosOWznowienie, prosOOdwolanie,
  } = useKontoWPrzerwie();
  if (!wPrzerwie && !zaplanowana && !rezygnacjaZaplanowana) return null;
  const data = (wartosc) => formatDate(wartosc, { locale: currentLanguage, fallback: '' });
  // Początek przerwy i rezygnacji to znacznik czasu (północ w Warszawie), koniec i termin to sam dzień.
  const dzien = (wartosc) => formatDate(wartosc, { locale: currentLanguage, fallback: '', timeZone: STREFA_FIRMY });

  if (rezygnacjaZaplanowana) {
    return (
      <Banner tone="info" compact icon={<LockIcon size={16} />} data-cy="account-resignation-scheduled" style={{ marginBottom: 'var(--sp-4)' }}>
        {t('rezygnacja.zaplanowana', { od: dzien(resignedAt) })}
      </Banner>
    );
  }
  if (zaplanowana) {
    return (
      <Banner tone="info" compact icon={<LockIcon size={16} />} data-cy="account-pause-scheduled" style={{ marginBottom: 'var(--sp-4)' }}>
        {t('przerwa.zaplanowana', { od: dzien(pausedAt) })}
      </Banner>
    );
  }
  if (powod === 'rezygnacja') {
    const usuniecie = data(dataDeletionFrom);
    return (
      <Banner
        tone="warning"
        icon={<LockIcon size={20} />}
        title={t('rezygnacja.tytul')}
        data-cy="account-resigned-banner"
        style={{ marginBottom: 'var(--sp-4)' }}
        action={administrator
          ? { label: t('rezygnacja.eksport.przycisk'), onClick: () => navigate('/company-export'), dataCy: 'account-resigned-export' }
          : undefined}
      >
        <Akapit>{t('rezygnacja.opis')}</Akapit>
        {usuniecie && (
          <Akapit data-cy="account-resigned-delete-from">
            {administrator
              ? t('rezygnacja.usuniecie', { od: usuniecie })
              : t('rezygnacja.usunieciePracownik', { od: usuniecie })}
          </Akapit>
        )}
        {prosOOdwolanie && (
          <LinkPrzycisk type="button" onClick={prosOOdwolanie} data-cy="account-resigned-cancel">
            {t('rezygnacja.odwolanie.przycisk')}
          </LinkPrzycisk>
        )}
      </Banner>
    );
  }
  const koniec = data(pausedUntil);
  return (
    <Banner
      tone="warning"
      icon={<LockIcon size={20} />}
      title={koniec ? t('przerwa.tytulDo', { do: koniec }) : t('przerwa.tytul')}
      data-cy="account-paused-banner"
      style={{ marginBottom: 'var(--sp-4)' }}
      action={prosOWznowienie ? { label: t('przerwa.wznowienie.przycisk'), onClick: prosOWznowienie, dataCy: 'account-paused-resume' } : undefined}
    >
      {t('przerwa.opis')}
    </Banner>
  );
}

const Akapit = styled.p`
  margin: 0 0 var(--sp-2);
`;

// Druga akcja banera jako odnośnik w treści: Banner ma miejsce na jeden przycisk, a kolor dziedziczy
// z tonu (`currentColor`), jak przycisk akcji.
const LinkPrzycisk = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.75;
  }
`;

// `disabled` na fieldsecie wyłącza naraz wszystkie kontrolki w środku (wzorzec blokady Premium).
// `display: contents`, żeby opakowanie nie zmieniało układu strony (siatki, flex).
const Pole = styled.fieldset`
  display: contents;
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

/** Formularz zapisu: w trybie tylko do odczytu (przerwa, rezygnacja) pola i przyciski w środku są wyłączone. */
export function TylkoOdczytWPrzerwie({ children, 'data-cy': dataCy }) {
  const { wPrzerwie } = useKontoWPrzerwie();
  return (
    <Pole disabled={wPrzerwie} data-cy={dataCy} data-konto-w-przerwie={wPrzerwie ? 'tak' : undefined}>
      {children}
    </Pole>
  );
}
